<template>
  <div class="whatsapp-templates-modal">
    <div v-if="isLoading" class="is-flex is-justify-content-center is-align-items-center is-flex-direction-column">
      <div class="loading"></div>
      <p>טוען נתונים...</p>
    </div>

    <div v-else>
      <!-- Alerts Section - Similar to send_cv modal -->
      <CvOfferAlerts
        @setRedAlert="setRedAlert($event)"
        @warningsStatus="warningsStatus"
        :offer="offer"
        :validation="false"
      />

      <!-- Alert Status Display -->
      <div class="alert-status-section mb-4">
        <div v-if="hasAlerts" class="notification is-warning" style="text-align: right;">
          <strong>התראות קיימות:</strong>
          <ul class="mt-2">
            <li v-for="alert in displayedAlerts" :key="alert.id">{{ alert.message }}</li>
          </ul>
        </div>
        <div v-else class="notification is-info" style="text-align: right;">
          <strong>לא קיימת התראה</strong>
        </div>
      </div>

      <!-- Template Selection -->
      <div class="template-selection-section" style="text-align: right;">
        <h4 class="title is-5">בחר נוסח הודעה:</h4>

        <!-- Template Options -->
        <div class="field">
          <div class="control">
            <label class="radio">
              <input type="radio" v-model="selectedTemplate" value="interest" />
              <span class="mr-2">נוסח מס' 1 - הודעת בדיקת התעניינות</span>
            </label>
          </div>
        </div>

        <div class="field">
          <div class="control">
            <label class="radio">
              <input type="radio" v-model="selectedTemplate" value="infoRequest" />
              <span class="mr-2">נוסח מס' 2 - הודעת בקשה לקבלת מידע לבדיקת התאמה</span>
            </label>
          </div>
        </div>

        <div class="field">
          <div class="control">
            <label class="radio">
              <input type="radio" v-model="selectedTemplate" value="conversationEnd" />
              <span class="mr-2">נוסח מס' 3 - הודעת סיום שיחה</span>
            </label>
          </div>
        </div>

        <div class="field">
          <div class="control">
            <label class="radio">
              <input type="radio" v-model="selectedTemplate" value="custom" />
              <span class="mr-2">נוסח מותאם אישית</span>
            </label>
          </div>
        </div>
      </div>

      <!-- Message Edit -->
      <div v-if="selectedTemplate" class="message-edit-section mt-4" style="text-align: right;">
        <div class="notification is-info is-light">
          <p><strong>הודעה לאחר מילוי פרטים:</strong></p>
          <div class="field mt-3">
            <div class="control">
              <textarea
                class="textarea message-editable"
                v-model="processedMessageEditable"
                rows="6"
                dir="rtl"
                style="text-align: right; color: #363636 !important; background: white !important; border: 1px solid #dbdbdb;"
                placeholder="הזן הודעה..."
              ></textarea>
            </div>
          </div>
        </div>
      </div>

      <!-- Action Buttons -->
      <div class="modal_footer is-justify-content-space-between mt-4">
        <div class="is-flex is-align-items-center">
          <button
            @click="sendWhatsappMessage"
            :disabled="isBlocked || !selectedTemplate || !processedMessageEditable.trim()"
            :title="isBlocked ? blockedMessage : ''"
            :class="{ 'is-loading': isSending }"
            class="button is-primary"
          >
            שלח הודעת WhatsApp
          </button>
        </div>

        <div class="is-flex is-align-items-center">
          <button
            @click="saveTemplate"
            v-if="selectedTemplate !== 'custom' && hasTemplateChanges"
            class="button is-info ml-2"
          >
            שמור תבנית למשרה
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapMutations } from 'vuex'
import CvOfferAlerts from '@/components/CvOfferAlerts.vue'
import OffersHelper from '@/custom/classes/offers_helper.js'
import { normalizeIsraeliPhoneForWhatsApp, WHATSAPP_INVALID_PHONE_MESSAGE } from '@/custom/classes/my_utils.js'
import { BLOCKED_TOAST, isApplicantBlocked } from '@/custom/helpers/applicant_block.js'

export default {
  name: 'ModalOfferWhatsappTemplates',
  components: {
    CvOfferAlerts
  },
  props: {
    payload: {
      type: Object,
      required: true
    }
  },
  data () {
    return {
      isLoading: true,
      isSending: false,
      blockedMessage: BLOCKED_TOAST,
      offer: null,
      position: null,
      selectedTemplate: '',
      finalMessage: '',
      customMessage: '',
      hasAlerts: false,
      displayedAlerts: [],
      offers_helper: new OffersHelper(),
      templates: {
        interest: '',
        infoRequest: '',
        conversationEnd: ''
      },
      defaultTemplates: {
        interest: 'היי [שם המועמד], מה שלומך?\nזו [שם פרטי] מ-[שם האופרציה]. ורציתי לבדוק האם משרה של [שם המשרה] יכולה לעניין אותך?',
        infoRequest: '',
        conversationEnd: ''
      }
    }
  },
  computed: {
    // The blocked banner itself is rendered by CvOfferAlerts (applicant json = this.offer.applicant).
    isBlocked () {
      return isApplicantBlocked(this.offer && this.offer.applicant)
    },
    processedMessage () {
      if (!this.finalMessage || !this.offer) return ''

      let message = this.finalMessage
      const applicant = this.offer.applicant
      const position = this.offer.position
      const operationName = (this.$settings && this.$settings.get({ path: 'app_name' })) || 'Rebase'

      // Replace placeholders with actual data
      message = message.replace(/\[שם המועמד\]/g, applicant.first_name || '[שם המועמד]')
      message = message.replace(/\[שם פרטי\]/g, (this.$utils && this.$utils.getOwner() && this.$utils.getOwner().first_name) || '[שם פרטי]')
      message = message.replace(/\[שם האופרציה\]/g, operationName)
      message = message.replace(/\[שם המשרה\]/g, position.title || '[שם המשרה]')
      message = message.replace(/\[קישור לעמוד המשרות של אופרציה רלווונטית\]/g, this.getJobsPageLink())

      return message
    },
    processedMessageEditable: {
      get () {
        return this.processedMessage
      },
      set (value) {
        // When user edits the processed message, we need to update the finalMessage
        // This is a simplified approach - in a more complex scenario you might want to
        // reverse the placeholder replacement
        this.finalMessage = value
      }
    },
    hasTemplateChanges () {
      if (this.selectedTemplate === 'custom') return false
      const originalTemplate = this.getOriginalTemplate(this.selectedTemplate)
      return this.finalMessage !== originalTemplate
    }
  },
  watch: {
    selectedTemplate (newTemplate) {
      if (newTemplate === 'custom') {
        this.finalMessage = this.customMessage
      } else {
        this.finalMessage = this.getTemplateContent(newTemplate)
      }
    },
    finalMessage (newMessage) {
      if (this.selectedTemplate === 'custom') {
        this.customMessage = newMessage
      }
    }
  },
  async created () {
    await this.init()
  },
  methods: {
    async init () {
      try {
        this.isLoading = true
        this.offer = this.payload.data || this.payload

        // Fetch position details with templates
        this.position = await this.$network_utils.getPosition({ id: this.offer.position.id })

        // Ensure nested company exists on offer.position — child components
        // (CvOfferAlerts) and offers_helper read offer.position.company directly,
        // but some callers pass it only as a top-level key or omit it.
        const company = (this.position && this.position.company) ||
          (this.offer.position && this.offer.position.company) ||
          this.offer.company
        this.offer = {
          ...this.offer,
          position: { ...this.offer.position, ...this.position, company }
        }

        // Set up templates
        this.templates = {
          interest: this.position.whatsapp_template_interest || this.defaultTemplates.interest,
          infoRequest: this.position.whatsapp_template_info_request || this.defaultTemplates.infoRequest,
          conversationEnd: this.position.whatsapp_template_conversation_end || this.getDefaultConversationEndTemplate()
        }

        // Check for alerts
        await this.checkAlerts()
      } catch (error) {
        console.error('Error initializing WhatsApp templates modal:', error)
        this.$utils.emit('show_toast', { text: 'שגיאה בטעינת הנתונים' })
      } finally {
        this.isLoading = false
      }
    },

    async checkAlerts () {
      try {
        // This would be similar to the send_cv alert checking logic
        const alerts = await this.$network_utils.getResourceAlerts({
          current_resource_type: 'Offer',
          current_resource_id: this.offer.id,
          designator: 'whatsapp_offer'
        })

        this.hasAlerts = alerts && alerts.length > 0
        this.displayedAlerts = alerts || []
      } catch (error) {
        console.error('Error checking alerts:', error)
        this.hasAlerts = false
        this.displayedAlerts = []
      }
    },

    getTemplateContent (templateType) {
      switch (templateType) {
        case 'interest':
          return this.templates.interest || this.defaultTemplates.interest
        case 'infoRequest':
          return this.templates.infoRequest || this.defaultTemplates.infoRequest
        case 'conversationEnd':
          return this.templates.conversationEnd || this.getDefaultConversationEndTemplate()
        default:
          return ''
      }
    },

    getOriginalTemplate (templateType) {
      switch (templateType) {
        case 'interest':
          return this.position.whatsapp_template_interest || this.defaultTemplates.interest
        case 'infoRequest':
          return this.position.whatsapp_template_info_request || this.defaultTemplates.infoRequest
        case 'conversationEnd':
          return this.position.whatsapp_template_conversation_end || this.getDefaultConversationEndTemplate()
        default:
          return ''
      }
    },

    getDefaultConversationEndTemplate () {
      const operationName = (this.$settings && this.$settings.get({ path: 'app_name' }) && this.$settings.get({ path: 'app_name' }).toLowerCase()) || 'suitup'

      switch (operationName) {
        case 'suitup':
          return `תודה על העדכון. בכל מקרה, אם יש לך חבר/ה שיתעניין לדעתך במשרה נשמח לסייע
ותמיד אפשר לצפות ולהגיש מועמדות למשרות באתר שלנו [קישור לעמוד המשרות של אופרציה רלווונטית]
וכדאי לעקוב אחרינו:
  בפייסבוק👍  https://www.facebook.com/suitup.co.il
  בלינקדאין👍  https://www.linkedin.com/company/suitup-io
ובאינסטגרם👍  https://www.instagram.com/suitup.co.il`

        case 'rebase':
          return `תודה רבה על העדכון.
בכל מקרה, תמיד אפשר לצפות ולהגיש מועמדות למשרות באתר שלנו. אני מצרפת עבורך קישורים למערכת Rebase שם המשרות שלנו מתעדכנות כל הזמן👷🏼
כדאי לעדכן את הפרטים שלך ולהגיש מועמדות למשרות נוספות הרלוונטיות עבורך
 https://web.rebase.co.il   
⚡יש לך חברים שמחפשים עבודה בתחום? אנחנו נשמח לסייע גם להם (כדאי שיציינו שהגיעו דרכך לטובת מבצע חבר מביא חבר שלנו 😏)
המשך יום נעים, Rebase :)`

        case 'piola':
          return `תודה על העדכון. בכל מקרה, אם יש לך חבר/ה שיתעניין לדעתך במשרה נשמח לסייע
ותמיד אפשר לצפות ולהגיש מועמדות למשרות באתר שלנו [קישור לעמוד המשרות של אופרציה רלווונטית]
וכדאי לעקוב אחרינו:
  בפייסבוק👍  https://www.facebook.com/PiolaHR
  בלינקדאין👍  https://www.linkedin.com/company/78366537/
ובאינסטגרם👍  https://www.instagram.com/piola_hr`

        default:
          return 'תודה על העדכון. בכל מקרה, אם יש לך חבר/ה שיתעניין לדעתך במשרה נשמח לסייע.'
      }
    },

    getJobsPageLink () {
      const operationName = (this.$settings && this.$settings.get({ path: 'app_name' }) && this.$settings.get({ path: 'app_name' }).toLowerCase()) || 'rebase'

      switch (operationName) {
        case 'rebase':
          return 'https://web.rebase.co.il'
        case 'suitup':
          return 'https://suitup.co.il/jobs'
        case 'piola':
          return 'https://piola.co.il/jobs'
        default:
          return '[קישור לעמוד המשרות]'
      }
    },

    formatPhoneNumberForWhatsApp (phoneNumber) {
      return normalizeIsraeliPhoneForWhatsApp(phoneNumber)
    },

    async saveTemplate () {
      if (this.selectedTemplate === 'custom') return

      try {
        const templateField = `whatsapp_template_${this.selectedTemplate === 'infoRequest' ? 'info_request' : this.selectedTemplate === 'conversationEnd' ? 'conversation_end' : this.selectedTemplate}`

        await this.$network_utils.updatePosition({
          id: this.position.id,
          [templateField]: this.finalMessage
        })

        this.$utils.emit('show_toast', { text: 'התבנית נשמרה בהצלחה' })
      } catch (error) {
        console.error('Error saving template:', error)
        this.$utils.emit('show_toast', { text: 'שגיאה בשמירת התבנית' })
      }
    },

    async sendWhatsappMessage () {
      if (this.isBlocked) {
        this.$utils.emit('show_toast', { text: BLOCKED_TOAST })
        return
      }
      if (!this.selectedTemplate || !this.finalMessage.trim()) return

      try {
        this.isSending = true

        // Use the existing WhatsApp functionality
        const whatsappPayload = this.offers_helper.getWhatsAppLinkPayload(this.offer, 'positionOfferMessage')

        // Format phone number with Israeli country code
        const formattedPhoneNumber = this.formatPhoneNumberForWhatsApp(whatsappPayload.phoneNumber)
        if (!formattedPhoneNumber) {
          this.$utils.emit('show_toast', { text: WHATSAPP_INVALID_PHONE_MESSAGE })
          return
        }
        const message = this.processedMessageEditable

        // Open WhatsApp with the processed message and formatted phone number
        const whatsappUrl = `https://api.whatsapp.com/send?phone=${encodeURIComponent(formattedPhoneNumber)}&text=${encodeURIComponent(message)}`
        window.open(whatsappUrl, '_blank')

        // Close the modal
        this.remove(this.payload)

        this.$utils.emit('show_toast', { text: 'הודעת WhatsApp נשלחה בהצלחה' })
      } catch (error) {
        console.error('Error sending WhatsApp message:', error)
        this.$utils.emit('show_toast', { text: 'שגיאה בשליחת הודעת WhatsApp' })
      } finally {
        this.isSending = false
      }
    },

    setRedAlert (offer) {
      // Handle red alert from CvOfferAlerts component
    },

    warningsStatus (status) {
      // Handle warnings status from CvOfferAlerts component
    },

    ...mapMutations('modals', ['remove'])
  }
}
</script>

<style scoped>
.whatsapp-templates-modal {
  max-width: 800px;
  margin: 0 auto;
}

.alert-status-section {
  border-bottom: 1px solid #e0e0e0;
  padding-bottom: 15px;
}

.template-selection-section {
  padding: 15px 0;
}

.message-edit-section {
  border-top: 1px solid #e0e0e0;
  padding-top: 15px;
}

.message-editable {
  border: 1px solid #dbdbdb;
  border-radius: 4px;
  font-family: inherit;
}

.radio {
  display: flex;
  align-items: center;
  margin-bottom: 10px;
  cursor: pointer;
}

.radio input[type="radio"] {
  margin-left: 8px;
}

.message-with-details {
  font-family: monospace;
  direction: rtl;
  text-align: right;
}

.modal_footer {
  border-top: 1px solid #e0e0e0;
  padding-top: 15px;
}

.loading {
  border: 4px solid #f3f3f3;
  border-top: 4px solid #3498db;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  animation: spin 2s linear infinite;
  margin: 20px auto;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}
</style>
